import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { PlayerWrapper } from './playerWrapper';
import awsConfig from '../../../aws-exports';
import { DRMTYPE, Period } from '../../../utils/types';

import './videoPlayer.css';
import { useFeatures } from 'flagged';

dayjs.extend(isBetween);

/**
 * Configuration object for the Bitmovin video player.
 *
 * @constant
 * @type {Object}
 * @property {string} key - The license key for Bitmovin, retrieved from AWS configuration.
 * @property {Object} playback - Playback settings for the video player.
 * @property {boolean} playback.muted - Indicates whether the video should start muted.
 * @property {Object} style - Styling options for the video player.
 * @property {string} style.aspectratio - The aspect ratio of the video player.
 * @property {boolean} style.ux - Indicates whether the default Bitmovin UX should be used.
 * @property {string} style.width - The width of the video player.
 */
const BITMOVINCONFIG = {
	key: awsConfig.bitmovinLicenseKey,
	playback: {
		muted: true
	},
	style: {
		aspectratio: '16:9',
		ux: false,
		width: '100%'
	}
};

/**
 * VideoPlayerMulti component is a multi-period video player that uses Bitmovin player.
 * It initializes and manages the video playback for multiple video sources.
 *
 * @component
 * @param {VideoPlayerMultiProps} props - The properties for the VideoPlayerMulti component.
 * @param {boolean} [props.autoplay=true] - Whether the video should autoplay.
 * @param {string} props.drmType - The type of DRM to be used.
 * @param {function} props.onPeriodUpdate - Callback function to be called when the period updates.
 * @param {string} [props.height='400px'] - The height of the video player.
 * @param {string} props.laSource - The license acquisition source.
 * @param {Array} props.sources - The array of video sources.
 * @param {number} props.startPeriodIdx - The starting index for the video periods.
 *
 * @returns {JSX.Element} The VideoPlayerMulti component.
 */
export const VideoPlayerMulti: FC<VideoPlayerMultiProps> = (props) => {
	const { autoplay = true, drmType, height = '400px', laSource, onPeriodUpdate, sources, startPeriodIdx } = props;

	const { defaultPackagingConfiguration: defaultPackagingConfigurationFlag } = useFeatures();
	const defaultPackagingConfiguration = defaultPackagingConfigurationFlag.toString();

	const playerEl = useRef<HTMLDivElement>(null);
	const player = useRef<PlayerWrapper>();
	const playingPeriodIdx = useRef<number>(0);
	const [periodUpdate, setPeriodUpdate] = useState<number>(0);

	const setupPlayer = useCallback(() => {
		if (!player.current) {
			const playerConfig = {
				...BITMOVINCONFIG,
				style: { ...BITMOVINCONFIG.style, height },
				events: {
					playbackfinished: (): void => {
						const currentPlayer = player.current;
						const nextPeriodIdx = playingPeriodIdx.current + 1;

						if (currentPlayer && nextPeriodIdx < sources.length && sources[nextPeriodIdx] && (sources[nextPeriodIdx].endpoints || sources[nextPeriodIdx].endpoint)) {
							const nextEndpoint = sources[nextPeriodIdx].endpoints ? sources[nextPeriodIdx].endpoints.find((endpoint) => endpoint.format === defaultPackagingConfiguration)?.url : sources[nextPeriodIdx].endpoint;
							currentPlayer.init(nextEndpoint || '', undefined, undefined, autoplay, drmType, laSource, false);
							playingPeriodIdx.current++;
							setPeriodUpdate(playingPeriodIdx.current);
							onPeriodUpdate(playingPeriodIdx.current);
						} else if (currentPlayer && nextPeriodIdx >= sources.length) {
							currentPlayer.player.pause();
						}
					}
				}
			};

			const playerWrapper = new PlayerWrapper(playerEl.current!, playerConfig);
			player.current = playerWrapper;
		}
	}, [autoplay, drmType, height, laSource, onPeriodUpdate, sources, defaultPackagingConfiguration]);

	useEffect(() => {
		setupPlayer();
	}, [setupPlayer]);

	useEffect(() => {
		const currentPlayer = player.current;
		const periodIdx = playingPeriodIdx.current;

		if (currentPlayer && sources[periodIdx] && (sources[periodIdx].endpoints || sources[periodIdx].endpoint)) {
			const endpoint = sources[periodIdx].endpoints ? sources[periodIdx].endpoints.find((endpoint) => endpoint.format === defaultPackagingConfiguration)?.url : sources[periodIdx].endpoint;
			currentPlayer.init(endpoint || '', undefined, undefined, autoplay, drmType, laSource, false);
		}
	}, [autoplay, drmType, laSource, periodUpdate, sources, defaultPackagingConfiguration]);

	useEffect(() => {
		playingPeriodIdx.current = startPeriodIdx;
		setPeriodUpdate(startPeriodIdx);
	}, [startPeriodIdx]);

	return <div ref={playerEl}></div>;
};

type VideoPlayerMultiProps = {
	autoplay?: boolean;
	drmType?: DRMTYPE;
	height?: string;
	laSource?: string;
	onPeriodUpdate: (periodIdx: number) => void;
	sources: Period[];
	startPeriodIdx: number;
};

export default VideoPlayerMulti;
